import { Skeleton } from "@veritrellis/ui";
import { useLayoutEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { useAdminPageMeta } from "../hooks/useAdminPageMeta.js";
import { PolicyJsonEditor } from "../components/policyBuilder/PolicyJsonEditor.js";
import { PolicySimulationPanel } from "../components/policyBuilder/PolicySimulationPanel.js";
import { usePersistedAdminEnvironment } from "../hooks/usePersistedAdminEnvironment.js";
import { useWorkspaceId } from "../hooks/useWorkspaceId.js";
import { envLabel } from "../lib/requestPresentation.js";

const DEFAULT_DRAFT = JSON.stringify(
  {
    mode: "conditional",
    rules: [
      {
        when: [{ field: "amount", op: "gte", value: 500 }],
        then: "require_approval"
      }
    ],
    default_decision: "allow"
  },
  null,
  2
);

export const PolicySimulatePage = () => {
  const { workspaceId, isLoading } = useWorkspaceId();
  const { actionType: routeAction } = useParams<{ actionType: string }>();
  const [search] = useSearchParams();
  const envParam = search.get("env");
  const [environment, setEnvironment] = usePersistedAdminEnvironment();

  useLayoutEffect(() => {
    if (envParam === "production" || envParam === "sandbox") {
      setEnvironment(envParam);
    }
  }, [envParam, setEnvironment]);

  const [actionType, setActionType] = useState<string>(routeAction ?? "issue_refund");
  const [draftJson, setDraftJson] = useState<string>(DEFAULT_DRAFT);
  const [jsonError, setJsonError] = useState<string | null>(null);

  const decodedRoute = routeAction !== undefined ? decodeURIComponent(routeAction) : "";

  useLayoutEffect(() => {
    if (decodedRoute.length > 0) {
      setActionType(decodedRoute);
    }
  }, [decodedRoute]);

  const handleJsonChange = (raw: string) => {
    setDraftJson(raw);
    try {
      JSON.parse(raw);
      setJsonError(null);
    } catch {
      setJsonError("Invalid JSON — check brackets, quotes, and trailing commas.");
    }
  };

  const pageMeta = useMemo(
    () => ({
      title: "Policy simulator",
      description: `Replay draft policy against historical ${actionType} requests in ${envLabel(environment)}.`,
      environmentScoped: false
    }),
    [actionType, environment]
  );

  useAdminPageMeta(pageMeta);

  const parsedMode = useMemo(() => {
    try {
      const p = JSON.parse(draftJson) as { mode?: string };
      if (
        p.mode === "allow" ||
        p.mode === "deny" ||
        p.mode === "require_approval" ||
        p.mode === "conditional"
      ) {
        return p.mode;
      }
    } catch {
      /* ignore */
    }
    return "conditional" as const;
  }, [draftJson]);

  if (isLoading) {
    return (
      <div className="admin-page-stack">
        <Skeleton style={{ height: 48, width: "50%" }} />
        <Skeleton style={{ height: 320, width: "100%" }} />
      </div>
    );
  }
  if (workspaceId === null) {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <div className="admin-page-stack">
      <Link
        to={`/policies/${encodeURIComponent(actionType)}?env=${environment}`}
        className="admin-breadcrumb"
      >
        ← {actionType}
      </Link>

      <div
        className="admin-policy-workspace"
        style={{ gridTemplateColumns: "minmax(0, 1fr) 24rem" }}
      >
        <PolicySimulationPanel
          workspaceId={workspaceId}
          actionType={actionType}
          environment={environment}
          draftJson={draftJson}
          showConfig
          onActionTypeChange={setActionType}
          onEnvironmentChange={setEnvironment}
          editPolicyHref={`/policies/${encodeURIComponent(actionType)}?env=${environment}`}
        />
        <PolicyJsonEditor
          jsonText={draftJson}
          onChange={handleJsonChange}
          jsonError={jsonError}
          builderCompatible={false}
          mode={parsedMode}
          onModeChange={(m) => {
            try {
              const p = JSON.parse(draftJson) as Record<string, unknown>;
              p.mode = m;
              handleJsonChange(JSON.stringify(p, null, 2));
            } catch {
              handleJsonChange(JSON.stringify({ mode: m }, null, 2));
            }
          }}
        />
      </div>
    </div>
  );
};
